import React from 'react';
import {Link} from 'react-router-dom';

function CheckIcon() {
  return <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>;
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <img src="/images/mydocumentsign-logo.png" alt="MyDocumentSign" className="h-16" />
          <div className="flex items-center gap-6">
            <a href="#features" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">Features</a>
            <a href="#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">How It Works</a>
            <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">Pricing</a>
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 font-medium">Sign In</Link>
            <Link to="/register" className="text-sm px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 tracking-tight leading-tight">
            Sign documents<br />
            <span className="text-emerald-600">faster than ever</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto">
            Upload, send, and sign documents in minutes — not days. Legally binding electronic signatures trusted by businesses everywhere.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-lg">
              Start Signing for Free
            </Link>
            <a href="#how-it-works" className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-lg">
              See How It Works
            </a>
          </div>
          <p className="mt-4 text-sm text-gray-400">No credit card required · Free forever for basic use</p>
        </div>
      </section>

      {/* Logos / Trust */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-gray-400 uppercase tracking-wider">Trusted by teams who value simplicity</p>
          <div className="mt-6 flex justify-center items-center gap-12 flex-wrap text-gray-300">
            <span className="text-2xl font-bold">Company</span>
            <span className="text-2xl font-bold">Startup</span>
            <span className="text-2xl font-bold">Agency</span>
            <span className="text-2xl font-bold">Enterprise</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Everything you need to close deals faster</h2>
            <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">Simple, secure, and built for the way you actually work.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📄', title: 'Upload & Prepare', desc: 'Upload any PDF. Drag and drop signature fields exactly where you need them.' },
              { icon: '✍️', title: 'Draw or Type Signatures', desc: 'Signers can draw their signature, type it, or use their saved signature — on any device.' },
              { icon: '👥', title: 'Multiple Signers', desc: 'Add as many signers as you need. Each gets a unique, secure signing link.' },
              { icon: '🔒', title: 'Legally Binding', desc: 'Every signature captures IP address, timestamp, and audit trail for full legal compliance.' },
              { icon: '📱', title: 'Works Everywhere', desc: 'Signers don\'t need an account. They click a link and sign — desktop, tablet, or phone.' },
              { icon: '⚡', title: 'Instant Notifications', desc: 'Know the moment a document is signed. Completed documents are available immediately.' },
            ].map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <div className="text-3xl mb-4">{f.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Three steps. That's it.</h2>
            <p className="mt-4 text-lg text-gray-500">Get documents signed in minutes, not days.</p>
          </div>
          <div className="space-y-12">
            {[
              { step: '1', title: 'Upload your document', desc: 'Upload a PDF and add the people who need to sign it.' },
              { step: '2', title: 'Place signature fields', desc: 'Drag signature, initial, and date fields to the right spots on the document.' },
              { step: '3', title: 'Send & track', desc: 'Hit send. Each signer gets a secure link. You\'ll know the moment they sign.' },
            ].map((s, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xl font-bold flex-shrink-0">{s.step}</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{s.title}</h3>
                  <p className="mt-1 text-gray-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Simple, transparent pricing</h2>
            <p className="mt-4 text-lg text-gray-500">Start free. Upgrade when you're ready.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Free */}
            <div className="border border-gray-200 rounded-xl p-8">
              <h3 className="text-lg font-semibold text-gray-900">Free</h3>
              <div className="mt-4"><span className="text-4xl font-bold text-gray-900">$0</span><span className="text-gray-500">/month</span></div>
              <p className="mt-2 text-sm text-gray-500">For individuals getting started</p>
              <Link to="/register" className="mt-6 block text-center px-6 py-2.5 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 font-medium">
                Get Started
              </Link>
              <ul className="mt-8 space-y-3">
                {['5 documents per month', 'Unlimited signers', 'Draw & type signatures', 'Email notifications', 'Audit trail'].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-gray-600"><CheckIcon />{f}</li>
                ))}
              </ul>
            </div>
            {/* Pro */}
            <div className="border-2 border-emerald-600 rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-600 text-white text-xs font-medium rounded-full">Most Popular</div>
              <h3 className="text-lg font-semibold text-gray-900">Pro</h3>
              <div className="mt-4"><span className="text-4xl font-bold text-gray-900">$12</span><span className="text-gray-500">/month</span></div>
              <p className="mt-2 text-sm text-gray-500">For professionals and small teams</p>
              <Link to="/register" className="mt-6 block text-center px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">
                Start Free Trial
              </Link>
              <ul className="mt-8 space-y-3">
                {['Unlimited documents', 'Unlimited signers', 'Custom branding', 'API access', 'Priority support', 'Team management', 'Bulk send'].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-gray-600"><CheckIcon />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-emerald-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to ditch the printer?</h2>
          <p className="mt-4 text-lg text-emerald-100">Join thousands of professionals who sign documents online with MyDocumentSign.</p>
          <Link to="/register" className="mt-8 inline-block px-8 py-3 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 font-semibold text-lg">
            Get Started Free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid sm:grid-cols-4 gap-8">
            <div>
              <span className="text-lg font-bold text-white">MyDocumentSign</span>
              <p className="mt-3 text-sm text-gray-400">Simple, secure electronic signatures for everyone.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:support@mydocumentsign.com" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} MyDocumentSign. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
